import { useQuery } from "@tanstack/react-query";
import { History, MapPin } from "lucide-react";
import Loader from "../../../components/loader/loader";
import { getPatientLoginHistory } from "../../../services/patient/loginHistoryService";

function PatientLoginHistory() {
  const { data, isLoading } = useQuery({
    queryKey: ["patient-login-history"],
    queryFn: async () => {
      const res = await getPatientLoginHistory();
      return res?.data?.data;
    },
    retry: false,
  });

  const formatDate = (date) => new Date(date).toLocaleString();

  const getDuration = (login, logout) => {
    if (!logout) return "Active";
    const diff = new Date(logout) - new Date(login);

    const minutes = Math.floor(diff / 60000);
    return `${minutes} min`;
  };

  const getBrowserName = (ua) => {
    if (!ua) return "-";
    if (ua.includes("Chrome")) return "Chrome";
    if (ua.includes("Firefox")) return "Firefox";
    if (ua.includes("Safari")) return "Safari";
    if (ua.includes("Postman")) return "Postman";
    return "Unknown";
  };

  const openInGoogleMaps = (lat, lng) => {
    if (!lat || !lng) return;
    const url = `https://www.google.com/maps?q=${lat},${lng}`;
    window.open(url, "_blank");
  };

  return (
    <div className="p-6">
      {isLoading && <Loader />}

      <div className="flex items-center gap-2 mb-6">
        <History size={22} className="text-blue-600" />
        <h1 className="text-xl font-semibold text-gray-800">Login History</h1>
      </div>

      <div className="overflow-x-auto bg-white rounded-2xl shadow-sm border">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-gray-50 border-b text-gray-600">
            <tr>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Browser</th>
              <th className="px-4 py-3 font-medium">IP Address</th>
              <th className="px-4 py-3 font-medium">Login Time</th>
              <th className="px-4 py-3 font-medium">Logout Time</th>
              <th className="px-4 py-3 font-medium">Duration</th>
              <th className="px-4 py-3 font-medium">Location</th>
            </tr>
          </thead>

          <tbody className="divide-y">
            {data?.length === 0 && (
              <tr>
                <td colSpan="7" className="text-center py-6 text-gray-500">
                  No login history found
                </td>
              </tr>
            )}

            {data?.map((session) => (
              <tr
                key={session.id}
                className={`hover:bg-gray-50 transition ${
                  session.is_active ? "bg-green-50/40" : ""
                }`}
              >
                {/* Status */}
                <td className="px-4 py-3">
                  {session.is_active ? (
                    <span className="px-3 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                      Active
                    </span>
                  ) : (
                    <span className="px-3 text-nowrap py-1 text-xs font-medium bg-red-100 text-gray-600 rounded-full">
                      Logged Out
                    </span>
                  )}
                </td>

                {/* Browser */}
                <td className="px-4 py-3">
                  {getBrowserName(session.browser_name)}
                </td>

                {/* IP */}
                <td className="px-4 py-3">{session.ip_address || "-"}</td>

                {/* Login */}
                <td className="px-4 py-3">{formatDate(session.login_at)}</td>

                {/* Logout */}
                <td className="px-4 py-3">
                  {session.logout_at
                    ? formatDate(session.logout_at)
                    : "Still Active"}
                </td>

                {/* Duration */}
                <td className="px-4 py-3">
                  {getDuration(session.login_at, session.logout_at)}
                </td>

                {/* Location */}
                <td className="px-4 flex justify-center ">
                  {session.latitude && session.longitude ? (
                    <button
                      type="button"
                      onClick={() =>
                        openInGoogleMaps(session?.latitude, session.longitude)
                      }
                      className="p-3"
                    >
                      <MapPin size={22} />
                    </button>
                  ) : (
                    "-"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PatientLoginHistory;
